import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Tag, Percent, DollarSign, 
  Edit2, Trash2, Loader2, Check, X, Calendar 
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const { token, isAdmin, loading: authLoading } = useAuth();

  const initialFormState = {
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '0',
    max_discount: '',
    usage_limit: '',
    per_user_limit: '1',
    valid_until: '',
    first_order_only: false
  };
  
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    // Wait for auth loading to complete before fetching
    if (authLoading) return;
    if (token) {
      fetchCoupons();
    }
  }, [authLoading, token]);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        description: formData.description,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        per_user_limit: parseInt(formData.per_user_limit) || 1,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        first_order_only: formData.first_order_only
      };

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${API}/coupons`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon created successfully');
      }

      setShowForm(false);
      setEditingCoupon(null);
      setFormData(initialFormState);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      min_order_amount: coupon.min_order_amount.toString(),
      max_discount: coupon.max_discount?.toString() || '',
      usage_limit: coupon.usage_limit?.toString() || '',
      per_user_limit: coupon.per_user_limit.toString(),
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      first_order_only: coupon.first_order_only
    });
    setShowForm(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const getStatusBadge = (coupon) => {
    if (coupon.status === 'inactive') {
      return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">Inactive</span>;
    }
    if (coupon.valid_until && new Date(coupon.valid_until) < new Date()) {
      return <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs">Expired</span>;
    }
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return <span className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs">Limit Reached</span>;
    }
    return <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs">Active</span>;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-coupons-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container-custom py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-muted-foreground hover:text-primary">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-3xl font-heading font-bold">Coupon Management</h1>
                <p className="text-muted-foreground mt-1">Create and manage discount coupons</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                setEditingCoupon(null);
                setFormData(initialFormState);
                setShowForm(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-coupon-btn"
            >
              <Plus className="w-4 h-4" />
              Add Coupon
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Coupon Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-heading font-bold mb-6">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Coupon Code *</label>
                    <Input
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      placeholder="e.g., SAVE20"
                      className="uppercase"
                      required
                      data-testid="coupon-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Discount Type *</label>
                    <select
                      name="discount_type"
                      value={formData.discount_type}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      required
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                      <option value="free_shipping">Free Shipping</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="e.g., Get 20% off on orders above ₹500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '(₹)'}
                    </label>
                    <Input
                      type="number"
                      name="discount_value"
                      value={formData.discount_value}
                      onChange={handleChange}
                      placeholder={formData.discount_type === 'percentage' ? '20' : '100'}
                      required
                      min="0"
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Minimum Order Amount (₹)</label>
                    <Input
                      type="number"
                      name="min_order_amount"
                      value={formData.min_order_amount}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                {formData.discount_type === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Maximum Discount (₹)</label>
                    <Input
                      type="number"
                      name="max_discount"
                      value={formData.max_discount}
                      onChange={handleChange}
                      placeholder="Leave empty for no cap"
                      min="0"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Usage Limit</label>
                    <Input
                      type="number"
                      name="usage_limit"
                      value={formData.usage_limit}
                      onChange={handleChange}
                      placeholder="Unlimited"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Per User Limit</label>
                    <Input
                      type="number"
                      name="per_user_limit"
                      value={formData.per_user_limit}
                      onChange={handleChange}
                      placeholder="1"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Valid Until</label>
                  <Input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="first_order_only"
                    id="first_order_only"
                    checked={formData.first_order_only}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="first_order_only" className="text-sm">
                    First order only (new customers)
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={formLoading} className="btn-primary flex-1">
                    {formLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : editingCoupon ? (
                      'Update Coupon'
                    ) : (
                      'Create Coupon'
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowForm(false);
                      setEditingCoupon(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Coupons List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Loading coupons...</p>
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-card">
            <Tag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">No coupons yet</h2>
            <p className="text-muted-foreground mb-6">Create your first discount coupon to attract customers</p>
            <Button 
              onClick={() => setShowForm(true)} 
              className="btn-primary"
            >
              Create Coupon
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((coupon) => (
              <div 
                key={coupon.id} 
                className="bg-white rounded-2xl p-6 shadow-card"
                data-testid={`coupon-card-${coupon.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      {coupon.discount_type === 'percentage' ? (
                        <Percent className="w-6 h-6 text-primary" />
                      ) : (
                        <DollarSign className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg font-mono">{coupon.code}</h3>
                      {getStatusBadge(coupon)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(coupon)}
                      className="p-2 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon.id)}
                      className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <p className="text-muted-foreground text-sm mb-4">{coupon.description || 'No description'}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount:</span>
                    <span className="font-semibold">
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount_value}%` 
                        : coupon.discount_type === 'free_shipping'
                        ? 'Free Shipping'
                        : `₹${coupon.discount_value}`
                      }
                      {coupon.max_discount && ` (max ₹${coupon.max_discount})`}
                    </span>
                  </div>
                  
                  {coupon.min_order_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Min. Order:</span>
                      <span>₹{coupon.min_order_amount}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Usage:</span>
                    <span>
                      {coupon.usage_count} / {coupon.usage_limit || '∞'}
                    </span>
                  </div>
                  
                  {coupon.valid_until && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expires:</span>
                      <span>{new Date(coupon.valid_until).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {coupon.first_order_only && (
                    <div className="flex items-center gap-1 text-primary">
                      <Check className="w-4 h-4" />
                      <span>First order only</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;
